import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import config from '../config';
import { websocketManager } from '../utils/websocket';
import { persistentStorage } from '../utils/storage';
import { pushNotificationManager } from '../utils/pushNotifications';

const useStyles = makeStyles(() => ({
    rootContainer: {
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'white',
        overflow: 'hidden',
        borderRadius: 30,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Nunito, Poppins, Open Sans, Inter, sans-serif',
        padding: '20px',
        boxSizing: 'border-box',
    },
    successIcon: {
        width: 62,
        height: 62,
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 40,
        position: 'relative',
    },
    checkmark: {
        width: 29.13,
        height: 22.60,
        background: 'white',
        mask: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'3\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'20,6 9,17 4,12\'/%3E%3C/svg%3E") no-repeat center',
        maskSize: 'contain',
    },
    successTitle: {
        color: '#C084FC',
        fontSize: 18.32,
        fontFamily: 'Poppins',
        fontWeight: '600',
        lineHeight: '18.32px',
        wordWrap: 'break-word',
        textAlign: 'center',
        marginBottom: 20,
    },
    successMessage: {
        width: '100%',
        maxWidth: 286,
        textAlign: 'center',
        color: '#B6B6B6',
        fontSize: 18,
        fontFamily: 'Poppins',
        fontWeight: '500',
        lineHeight: '24px',
        wordWrap: 'break-word',
        marginBottom: 30,
    },
    refreshButton: {
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        color: 'white',
        borderRadius: 10,
        padding: '12px 24px',
        fontSize: 16,
        fontFamily: 'Poppins',
        fontWeight: '600',
        textTransform: 'none',
        boxShadow: '0px 4px 12px rgba(192, 132, 252, 0.3)',
        '&:hover': {
            background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
        },
        '&:disabled': {
            background: '#E0E0E0',
            color: '#9E9E9E',
        },
    },
    logoutButton: {
        marginTop: 16,
        color: '#724F96',
        borderRadius: 10,
        padding: '10px 24px',
        fontSize: 15,
        fontFamily: 'Poppins',
        fontWeight: '600',
        textTransform: 'none',
        border: '1px solid #C084FC',
        background: 'white',
        '&:hover': {
            background: 'rgba(192, 132, 252, 0.08)',
        },
    },
}));

const VerificationPending = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    
    const { userData, role } = location.state || {};
    const [isChecking, setIsChecking] = useState(false);

    const handleLogout = async () => {
        try {
            if (userData?.user_id) {
                await axios.patch(`${config.API_BASE_URL}/user-status/logout`, {
                    user_id: userData.user_id,
                });
            }
        } catch (err) {
            console.error('Logout API error:', err);
        } finally {
            // Unregister FCM BEFORE clearing storage — we need currentUserId
            // in storage to identify the user↔device row to delete.
            try { await pushNotificationManager.unregisterForUser(); } catch (_) { /* ignore */ }
            try { await persistentStorage.clear?.(); } catch (_) { /* ignore */ }
            try { localStorage.clear(); } catch (_) { /* native build */ }
            navigate('/associates-login', { replace: true });
        }
    };

    // Function to check verification status (for manual check button)
    const checkVerificationStatus = async () => {
        if (!userData?.user_id) return;

        setIsChecking(true);
        try {
            const response = await axios.get(`${config.API_BASE_URL}/users/${userData.user_id}`);
            const u = response.data;
            if (!u) return;
            if (u.verification_status === 'verified') {
                // Unattached VSP-pool user has no hotel_id in storage — the
                // standard dashboards would crash on missing workspace keys.
                // Route to /awaiting-assignment instead until the VSP tags
                // them to a property or event.
                const hotelId = await persistentStorage.get('hotel_id');
                // Treat the literal strings "null" / "undefined" as empty.
                // Older sessions may have those stored thanks to the unguarded
                // localStorage.setItem('hotel_id', null) that lived in
                // AssociatesLogin until recently.
                const hasHotel = hotelId && hotelId !== 'null' && hotelId !== 'undefined';
                if (!hasHotel) {
                    navigate('/awaiting-assignment');
                } else if (role === 'supervisor') {
                    navigate('/supervisor-dashboard');
                } else if (role === 'valet' || role === 'serviceboy') {
                    navigate('/valet-dashboard');
                }
            } else if (u.verification_status === 'unverified') {
                // Send-back happened while user was on this screen. Mirror the
                // WS-driven path: route to /personal-details with the latest
                // verification_issue text so the banner renders immediately.
                navigate('/personal-details', {
                    state: {
                        userData: { ...userData, verification_issue: u.verification_issue },
                        role,
                    },
                });
            }
        } catch (error) {
            console.error('Error checking verification status:', error);
        } finally {
            setIsChecking(false);
        }
    };

    // WebSocket-based real-time verification status updates
    useEffect(() => {
        if (!userData?.user_id) return;

        console.log('[VerificationPending] Setting up WebSocket listener for user:', userData.user_id);

        // Connect to WebSocket
        websocketManager.connect();

        // Join user-specific room for real-time updates
        const userRoom = `user_${userData.user_id}`;
        websocketManager.joinRoom(userRoom);

        // Handle real-time verification status updates. The correct event is
        // employee_verification_update — user_status_change is presence-only
        // (online/offline) and never carries verification_status, so the old
        // listener was unreachable. Backend broadcasts to user_{id} so this
        // component sees its own approval without polling.
        const handleVerificationUpdate = async (data) => {
            console.log('[VerificationPending] Received verification update:', data);
            if (Number(data.user_id) !== Number(userData.user_id)) return;

            if (data.verification_status === 'verified') {
                console.log('[VerificationPending] User verified! Redirecting to dashboard...');
                // Unattached VSP-pool user (no hotel_id in storage) routes to
                // /awaiting-assignment instead of a dashboard that would crash
                // on missing workspace keys.
                const hotelId = await persistentStorage.get('hotel_id');
                // Treat the literal strings "null" / "undefined" as empty.
                // Older sessions may have those stored thanks to the unguarded
                // localStorage.setItem('hotel_id', null) that lived in
                // AssociatesLogin until recently.
                const hasHotel = hotelId && hotelId !== 'null' && hotelId !== 'undefined';
                if (!hasHotel) {
                    navigate('/awaiting-assignment');
                } else if (role === 'supervisor') {
                    navigate('/supervisor-dashboard');
                } else if (role === 'valet' || role === 'serviceboy') {
                    navigate('/valet-dashboard');
                }
            } else if (data.verification_status === 'unverified') {
                // Manager sent the submission back. Push to the resubmit form
                // (PersonalDetails reads location.state.userData for prefill
                // and renders the rejection banner from verification_issue).
                console.log('[VerificationPending] Sent back for resubmission. Routing to /personal-details');
                navigate('/personal-details', {
                    state: {
                        userData: { ...userData, verification_issue: data.verification_issue },
                        role,
                    },
                });
            }
        };

        websocketManager.addListener('employee_verification_update', handleVerificationUpdate);

        // Also check immediately when component mounts (fallback for missed events).
        checkVerificationStatus();

        // Cleanup function
        return () => {
            console.log('[VerificationPending] Cleaning up WebSocket listener');
            websocketManager.removeListener('employee_verification_update', handleVerificationUpdate);
            websocketManager.leaveRoom(userRoom);
        };
    }, [userData?.user_id, role, navigate]);

    return (
        <div className={classes.rootContainer}>
            <div className={classes.successIcon}>
                <div className={classes.checkmark} />
            </div>
            
            <div className={classes.successTitle}>
                Thankyou for submitting your details.
            </div>
            
            <div className={classes.successMessage}>
                Your login will be authorised after successful verification.
                <br />
              
            </div>
            
            <Button
                className={classes.refreshButton}
                onClick={checkVerificationStatus}
                disabled={isChecking}
                startIcon={isChecking ? <CircularProgress size={16} color="inherit" /> : null}
            >
                {isChecking ? 'Checking...' : 'Check Status Now'}
            </Button>

            <Button
                className={classes.logoutButton}
                onClick={handleLogout}
            >
                Logout
            </Button>
        </div>
    );
};

export default VerificationPending;
